import "@/App.css";
import "@/components/Logo";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { OfflineBanner } from "@/components/OfflineBanner";
import { SyncIndicator } from "@/components/SyncIndicator";
import { registerServiceWorker } from "@/lib/push";

import LoginPage from "@/pages/LoginPage";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import ManualPage from "@/pages/ManualPage";

import OwnerLayout from "@/pages/owner/OwnerLayout";
import OwnerAdmins from "@/pages/owner/OwnerAdmins";
import OwnerMessages from "@/pages/owner/OwnerMessages";
import OwnerHealth from "@/pages/owner/OwnerHealth";
import OwnerChurn from "@/pages/owner/OwnerChurn";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminTables from "@/pages/admin/AdminTables";
import AdminMenu from "@/pages/admin/AdminMenu";
import AdminWaiters from "@/pages/admin/AdminWaiters";
import AdminReports from "@/pages/admin/AdminReports";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminTableView from "@/pages/admin/AdminTableView";
import AdminInventory from "@/pages/admin/AdminInventory";
import AdminRecipes from "@/pages/admin/AdminRecipes";
import AdminInventoryDashboard from "@/pages/admin/AdminInventoryDashboard";
import AdminExpiredScreen from "@/pages/admin/AdminExpiredScreen";

import WaiterLayout from "@/pages/waiter/WaiterLayout";
import WaiterTables from "@/pages/waiter/WaiterTables";
import WaiterTableOrder from "@/pages/waiter/WaiterTableOrder";
import WaiterHistory from "@/pages/waiter/WaiterHistory";
import PrintTicket from "@/pages/PrintTicket";

import AdminDelivery from "@/pages/admin/AdminDelivery";
import CustomerStorefront from "@/pages/customer/CustomerStorefront";
import CustomerOrders from "@/pages/customer/CustomerOrders";

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "owner") return <Navigate to="/owner" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "waiter") return <Navigate to="/waiter" replace />;
  if (user.role === "customer") return <Navigate to="/customer/orders" replace />;
  return <Navigate to="/login" replace />;
}

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#fcfbfd]">
      <div className="flex flex-col items-center gap-4">
        <div className="animate-pulse">
          <Logo size={88} variant="plain" />
        </div>
        <p className="text-sm tracking-[0.3em] uppercase text-[#7248ae]">cargando…</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) {
    // redirect to user's panel
    const dest = user.role === "owner" ? "/owner" : user.role === "admin" ? "/admin" : "/waiter";
    return <Navigate to={dest} replace />;
  }
  // Gate the entire admin app when subscription is expired
  if (role === "admin" && user.subscription_active === false) {
    return <AdminExpiredScreen />;
  }
  return children;
}

function App() {
  useEffect(() => { registerServiceWorker(); }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <OfflineBanner />
        <SyncIndicator />
        <Toaster position="top-right" richColors closeButton />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/manual" element={<ManualPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/print/:orderId" element={<ProtectedRoute><PrintTicket /></ProtectedRoute>} />

          {/* Owner */}
          <Route path="/owner" element={<ProtectedRoute role="owner"><OwnerLayout /></ProtectedRoute>}>
            <Route index element={<OwnerAdmins />} />
            <Route path="messages" element={<OwnerMessages />} />
            <Route path="health" element={<OwnerHealth />} />
            <Route path="churn" element={<OwnerChurn />} />
          </Route>

          {/* Admin */}
          <Route path="/admin" element={<ProtectedRoute role="admin"><AdminLayout /></ProtectedRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="tables" element={<AdminTables />} />
            <Route path="tables/:tableId" element={<AdminTableView />} />
            <Route path="menu" element={<AdminMenu />} />
            <Route path="inventory" element={<AdminInventory />} />
            <Route path="recipes" element={<AdminRecipes />} />
            <Route path="inventory-ai" element={<AdminInventoryDashboard />} />
            <Route path="delivery" element={<AdminDelivery />} />
            <Route path="waiters" element={<AdminWaiters />} />
            <Route path="reports" element={<AdminReports />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>

          {/* Waiter */}
          <Route path="/waiter" element={<ProtectedRoute role="waiter"><WaiterLayout /></ProtectedRoute>}>
            <Route index element={<WaiterTables />} />
            <Route path="tables/:tableId" element={<WaiterTableOrder />} />
            <Route path="history" element={<WaiterHistory />} />
          </Route>

          {/* Public storefront + customer dashboard */}
          <Route path="/r/:slug" element={<CustomerStorefront />} />
          <Route path="/customer/orders" element={<ProtectedRoute role="customer"><CustomerOrders /></ProtectedRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
